import { SupplierFilterItem } from 'types';
import { Card, Text, Stack, Box, Divider, Anchor, Group } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import MultiRef from 'react-multi-ref';
import { useSearchFiltersContext } from 'context/SearchFilters.context';
import { handleSelection } from 'utils/filters/filters';
import { Actions, trackAction } from 'utils/amplitude';
import { NoResults } from '@icons';
import { PebbleButtonSet, PebbleCheckbox, PebbleCheckboxGroup } from 'components/ui';
import classes from './ProviderFilter.module.scss';
import { PebbleButtonsEnum } from 'enums';

const ProviderFilter: React.FC = () => {
  const [checkboxRefs] = useState(() => new MultiRef<number, HTMLInputElement>());

  const { selectedProviders, providers, providerSearchString, onFilterChange } =
    useSearchFiltersContext();

  const isIndeterminate = selectedProviders.length === 0;

  useEffect(() => {
    if (isIndeterminate) {
      checkboxRefs.map.forEach((checkbox) => {
        checkbox.indeterminate = true;
      });
    }
  }, [isIndeterminate, checkboxRefs]);

  const filteredProviders = useMemo<SupplierFilterItem[]>(() => {
    if (providerSearchString) {
      return providers.filter((provider) =>
        provider.name.toLowerCase().includes(providerSearchString.toLowerCase()),
      );
    }
    return providers;
  }, [providerSearchString, providers]);

  const label = useMemo<string>(() => {
    const singleMatch = filteredProviders.length === 1;
    if (providerSearchString && singleMatch) {
      return '1 match';
    }
    if (providerSearchString) {
      return `${filteredProviders.length} matches`;
    }
    return 'Filter by provider';
  }, [providerSearchString, filteredProviders]);

  if (providerSearchString && filteredProviders.length === 0) {
    return (
      <Stack className={classes.noResultsWrapper}>
        <NoResults size={94} className={classes.noResultsIcon} title="No Results" role="img" />
        <Text size="lg">We couldn't find any providers with that name</Text>
        <Divider className={classes.noResultsDivider} />
        <Text>Know a great activity provider?</Text>
        <Anchor
          href="https://docs.google.com/forms/d/e/1FAIpQLSfcMtbrqHy7pyEIWmi9DOQuC9pgXjELYnvw71jwI3hsZ511ig/viewform?usp=pp_url"
          onClick={() => trackAction(Actions.LET_US_KNOW_PROVIDER)}
        >
          Let us know and we'll get them on the list
        </Anchor>
      </Stack>
    );
  }

  return (
    <Box mt="md">
      <Group justify="space-between" className={classes.labelWrapper}>
        <Text className={classes.checkboxGroupLabel}>{label}</Text>
        {!providerSearchString && (
          <>
            <PebbleButtonSet
              btnVariant={PebbleButtonsEnum.TERTIARY}
              size="sm"
              disabled={selectedProviders.length === 0}
              onClick={() => {
                handleSelection([], 'provider', onFilterChange);
                trackAction(Actions.PROVIDER_SELECT_ALL);
              }}
              pr={0}
            >
              Select all
            </PebbleButtonSet>
          </>
        )}
      </Group>
      <Card py={0} className={classes.filterCard}>
        <Stack p={0}>
          <PebbleCheckboxGroup
            onChange={(val) => {
              handleSelection(val, 'provider', onFilterChange);
            }}
            value={selectedProviders}
          >
            {filteredProviders.map((provider) => (
              <PebbleCheckbox
                key={provider.id}
                labelPosition="left"
                fullWidth
                labelSizeMd
                isList
                value={provider.id}
                label={provider.name}
                indeterminate={isIndeterminate}
                onClick={() => {
                  trackAction(Actions.PROVIDER_SELECTED);
                }}
              />
            ))}
          </PebbleCheckboxGroup>
        </Stack>
      </Card>
    </Box>
  );
};

export default ProviderFilter;
